/**
 * Simple state manager for integration settings
 * Listens for existing component events and maps them to state
 */
import { dispatchEvent } from './event-manager';
/**
 * Initialize state management for a specific form
 *
 * @param formElement The form element to manage state for
 */
export const initStateManager = (formElement) => {
    // Listen for change events from components with data-state-control within this form
    formElement.addEventListener('change', handleComponentChange);
    // Set up direct event listeners for switch components (web components don't always bubble properly)
    setupDirectSwitchListeners(formElement);
    // Dispatch initial state for sections within this form
    dispatchInitialState(formElement);
};
/**
 * Set up direct event listeners for switch components
 *
 * @param formElement The form element to search within
 */
const setupDirectSwitchListeners = (formElement) => {
    const switches = formElement.querySelectorAll('uo-field-input-switch[data-state-control]');
    switches.forEach(switchElement => {
        switchElement.addEventListener('change', handleComponentChange);
    });
};
/**
 * Dispatch initial state events for sections to show correct visibility
 *
 * @param formElement The form element to search within
 */
export const dispatchInitialState = (formElement) => {
    const stateElements = formElement.querySelectorAll('[data-state-control]');
    stateElements.forEach(element => {
        const stateName = element.getAttribute('data-state-control');
        if (!stateName) {
            return;
        }
        const value = getElementValue(element);
        if (value) {
            setState(stateName, value);
        }
    });
};
/**
 * Handle component change events
 *
 * @param event The change event
 */
const handleComponentChange = (event) => {
    const element = event.target;
    const stateName = element.getAttribute('data-state-control');
    if (!stateName) {
        return;
    }
    // Get the value directly from the changed element
    const stateValue = getElementValue(element);
    if (stateValue !== undefined) {
        setState(stateName, stateValue);
    }
};
/**
 * Get the current value from an element
 *
 * @param element The element to get value from
 * @return The element's value or undefined
 */
const getElementValue = (element) => {
    // Handle web components
    if (element.tagName.includes('-')) {
        const webComponent = element;
        if (webComponent.tagName === 'UO-FIELD-INPUT-RADIO') {
            return webComponent.checked ? webComponent.value : undefined;
        }
        if (webComponent.tagName === 'UO-FIELD-INPUT-SWITCH') {
            return webComponent.checked ? '1' : '0';
        }
        // Add other web component types as needed
        return webComponent.value || (webComponent.checked ? 'checked' : undefined);
    }
    // Handle standard form elements
    if (element instanceof HTMLInputElement) {
        if (element.type === 'radio') {
            return element.checked ? element.value : undefined;
        }
        if (element.type === 'checkbox') {
            return element.checked ? 'checked' : 'unchecked';
        }
        return element.value;
    }
    return element.value;
};
/**
 * Set a state value
 *
 * @param stateName The state name
 * @param stateValue The state value
 */
export const setState = (stateName, stateValue) => {
    // Use centralized event system
    dispatchEvent('state-change', {
        state: stateName,
        value: stateValue
    }, false);
};
/**
 * Get current state value (legacy method)
 *
 * @param stateName The state name
 * @return The current state value
 */
export const getState = (stateName) => {
    // Find element with state name and get its current value
    const stateElement = document.querySelector(`[data-state-control="${stateName}"]`);
    if (stateElement && stateElement.state) {
        return stateElement.state;
    }
    return '';
};
